import { CATEGORY_COLORS } from '../../constants/categoryColors';
import { LOG_TYPES, getTimelineMetaForCategory, SPECIAL_FILTER_TYPES, isBehaviorIncidentEntry } from '../../constants/logTypeRegistry';
import { getCustomCategories, getIncidentTypeConfig } from '../../services/incidentService';
import { removeMoodSnapshotsFromEntries, replaceMoodSnapshotInEntries } from '../../services/timeline/moodSnapshotUtils';
import { buildOptimisticIncidentEntryBase, buildOptimisticMoodEntryBase } from './timelineOptimisticEntryFactories';
import { getEntryUser, normalizeDailyLogEntry } from './timelineSourceNormalizers';

const getQuickDailyLogTitle = (entry, categoryMeta) => {
  const text = (entry.title || entry.text || '').trim();
  if (!text) {
    return categoryMeta.titlePrefix;
  }

  const firstLine = text.split('\n')[0].trim();
  if (!firstLine) {
    return categoryMeta.titlePrefix;
  }

  return firstLine.length > 60 ? `${firstLine.slice(0, 57)}...` : firstLine;
};

const getQuickNoteMeta = (entry) => {
  if (entry.importantMoment) {
    return {
      type: 'importantMoment',
      timelineType: 'importantMoment',
      titlePrefix: SPECIAL_FILTER_TYPES.importantMoment.titlePrefix,
      color: CATEGORY_COLORS.importantMoment.dot,
      icon: SPECIAL_FILTER_TYPES.importantMoment.icon,
    };
  }

  return getTimelineMetaForCategory(entry.category, { importantMoment: !!entry.importantMoment });
};

export const normalizeIncidentCategory = (incidentConfig, incidentType) => {
  const normalizedType = incidentConfig?.id === 'behavioral'
    ? 'behavior'
    : (incidentConfig?.id || incidentType || 'other');

  if (incidentConfig?.id === 'behavioral') {
    return {
      categoryId: 'behavior',
      label: LOG_TYPES.behavior.displayLabel,
      color: LOG_TYPES.behavior.palette.dot,
      icon: LOG_TYPES.behavior.icon,
    };
  }

  return {
    categoryId: normalizedType,
    label: incidentConfig?.label || incidentType || 'Other',
    color: incidentConfig?.color || '#6B7280',
    icon: incidentConfig?.emoji || '📝',
  };
};

const isSameDay = (dateA, dateB) => (
  dateA.getFullYear() === dateB.getFullYear()
  && dateA.getMonth() === dateB.getMonth()
  && dateA.getDate() === dateB.getDate()
);

export const shouldApplyOptimisticEntry = ({ entry, childId, selectedDate }) => {
  if (!entry || entry.childId !== childId) {
    return false;
  }

  const entryTimestamp = entry.timestamp?.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp);
  if (Number.isNaN(entryTimestamp.getTime())) {
    return false;
  }

  return isSameDay(entryTimestamp, selectedDate);
};

export const applyOptimisticTimelineEntry = ({
  entry,
  prevEntries,
  customCategories,
}) => {
  const entryTimestamp = entry.timestamp?.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp);

  if (entry.collection === 'incidents') {
    const incidentMeta = getIncidentTypeConfig(entry.type, customCategories) || getIncidentTypeConfig('OTHER');
    const normalizedIncident = normalizeIncidentCategory(incidentMeta, entry.type);
    const optimisticIncident = buildOptimisticIncidentEntryBase({
      entry,
      timestamp: entryTimestamp,
      title: entry.title || entry.content || incidentMeta.label,
      content: entry.content || entry.notes || entry.triggerSummary || '',
      triggerSummary: entry.triggerSummary || entry.remedy || '',
      color: entry.color || normalizedIncident.color,
      icon: entry.icon || normalizedIncident.icon,
      incidentData: entry.incidentData && typeof entry.incidentData === 'object' && Object.keys(entry.incidentData).length > 0
        ? entry.incidentData
        : null,
      extra: {
        ...entry,
        titlePrefix: incidentMeta.label,
        incidentCategoryId: normalizedIncident.categoryId,
        incidentCategoryLabel: normalizedIncident.label,
        entryType: 'incident',
        originalData: entry.originalData || entry,
        ...getEntryUser(entry),
      },
    });

    return {
      ...prevEntries,
      incidents: [
        optimisticIncident,
        ...prevEntries.incidents.filter((incident) => incident.id !== optimisticIncident.id),
      ],
    };
  }

  if (entry.collection !== 'dailyLogs' && entry.collection !== 'dailyCare') {
    return prevEntries;
  }

  const isDeletedMoodEntry = entry.collection === 'dailyCare'
    && entry.actionType === 'mood'
    && entry.status === 'deleted';
  if (isDeletedMoodEntry) {
    const moodRemovalEntry = {
      ...entry,
      type: 'mood',
      actionType: 'mood',
    };

    return {
      ...prevEntries,
      dailyLogs: removeMoodSnapshotsFromEntries(prevEntries.dailyLogs, moodRemovalEntry),
      dailyHabits: removeMoodSnapshotsFromEntries(prevEntries.dailyHabits, moodRemovalEntry),
    };
  }

  const isBehaviorStyleEntry = isBehaviorIncidentEntry(entry);
  const isMoodEntry = entry.collection === 'dailyCare' && entry.actionType === 'mood';
  const categoryMeta = isMoodEntry
    ? getTimelineMetaForCategory('mood')
    : getQuickNoteMeta(entry);
  const incidentSnapshot = entry.contextSnapshot || entry.incidentData?.contextSnapshot || null;
  const severityLabel = entry.severityLabel || entry.incidentData?.severityLabel || null;
  const triggerSummary = entry.triggerSummary || entry.incidentData?.triggerSummary || null;
  const moodValue = entry.value || entry.data?.level || entry.mood || entry.moodLevel || entry.content || entry.text || 'Calm';

  if (isMoodEntry) {
    const optimisticMoodEntry = buildOptimisticMoodEntryBase({
      entry,
      timestamp: entryTimestamp,
      moodValue,
      notes: entry.notes || entry.incidentData?.notes || null,
      contextSnapshot: incidentSnapshot,
      incidentData: entry.incidentData && typeof entry.incidentData === 'object' && Object.keys(entry.incidentData).length > 0
        ? entry.incidentData
        : null,
      severityLabel,
      triggerSummary,
      remedy: entry.remedy || entry.incidentData?.remedy || null,
      extra: {
        ...entry,
        logCategory: categoryMeta.category || entry.logCategory || categoryMeta.type,
        color: LOG_TYPES.mood.palette.dot,
        categoryIcon: LOG_TYPES.mood.icon,
        incidentStyle: false,
        entryType: 'mood',
        incidentCategoryId: 'mood',
        incidentCategoryLabel: 'Mood',
        incidentCategoryColor: LOG_TYPES.mood.palette.dot,
        incidentCategoryIcon: LOG_TYPES.mood.icon,
        label: 'Mood',
        moodEmoji: LOG_TYPES.mood.icon,
        isImportantMoment: !!entry.importantMoment,
        value: String(moodValue),
        mood: String(moodValue),
        ...getEntryUser(entry),
      },
    });

    return {
      ...prevEntries,
      dailyLogs: removeMoodSnapshotsFromEntries(prevEntries.dailyLogs, optimisticMoodEntry),
      dailyHabits: replaceMoodSnapshotInEntries(prevEntries.dailyHabits, optimisticMoodEntry),
    };
  }

  const optimisticDailyLog = normalizeDailyLogEntry({
    ...entry,
    logCategory: categoryMeta.category || entry.logCategory || categoryMeta.type,
    timestamp: entryTimestamp,
    title: getQuickDailyLogTitle(entry, categoryMeta),
    titlePrefix: categoryMeta.titlePrefix,
    color: isBehaviorStyleEntry
      ? LOG_TYPES.behavior.palette.dot
      : categoryMeta.color,
    categoryIcon: isBehaviorStyleEntry
      ? LOG_TYPES.behavior.icon
      : categoryMeta.icon,
    incidentStyle: isBehaviorStyleEntry,
    entryType: isBehaviorStyleEntry ? 'incident' : entry.entryType,
    contextSnapshot: incidentSnapshot,
    incidentData: entry.incidentData && typeof entry.incidentData === 'object' && Object.keys(entry.incidentData).length > 0
      ? entry.incidentData
      : null,
    severity: entry.severity,
    severityLabel,
    triggerSummary,
    remedy: entry.remedy || entry.incidentData?.remedy || null,
    incidentCategoryId: isBehaviorStyleEntry ? 'behavior' : entry.incidentCategoryId,
    incidentCategoryLabel: isBehaviorStyleEntry
      ? 'Behavior'
      : entry.incidentCategoryLabel,
    incidentCategoryColor: isBehaviorStyleEntry
      ? LOG_TYPES.behavior.palette.dot
      : entry.incidentCategoryColor,
    incidentCategoryIcon: isBehaviorStyleEntry
      ? undefined
      : entry.incidentCategoryIcon,
    isImportantMoment: !!entry.importantMoment,
    ...getEntryUser(entry),
  });

  return {
    ...prevEntries,
    dailyLogs: [
      optimisticDailyLog,
      ...removeMoodSnapshotsFromEntries(prevEntries.dailyLogs, optimisticDailyLog),
    ],
  };
};

export const shouldHandleTimelineRefresh = ({ detail, childId }) => (
  !!detail && detail.childId === childId
);

export const getTimelineRefreshDates = (detail = {}) => Array.from(new Set(
  [detail.entryDate, ...(Array.isArray(detail.entryDates) ? detail.entryDates : [])]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
));
